/**
 * Renders the Rovo insights banner card.
 * Displays a Rovo hex icon, generated date, title, and a chevron-right indicator.
 * Invokes a callback when clicked.
 */
import React, { useCallback, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box, Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useFireAnalyticsEvent } from '../../common/analytics/use-fire-analytics';
import { commonMessages } from '../../common/messages/common-messages';
import { PulseTile } from '../../common/ui/pulse-tile';
import { formatGeneratedDate } from '../../common/utils/format-generated-date';

import { messages } from './messages';

const styles = cssMap({
	cardWrapper: {
		width: '100%',
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface.sunken'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	pressable: {
		backgroundColor: 'transparent',
		width: '100%',
		cursor: 'pointer',
		textAlign: 'start',
	},
	pressableWithExtraPadding: {
		backgroundColor: 'transparent',
		width: '100%',
		cursor: 'pointer',
		textAlign: 'start',
		paddingTop: token('space.075'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
	},
	textContainer: {
		flexGrow: 1,
		minWidth: '0',
	},
	iconTileSurface: {
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.tile'),
		flexShrink: 0,
	},
	chevron: {
		flexShrink: 0,
	},
});

type InsightsBannerAppearance = 'quickActions' | 'yourWork';

interface InsightsBannerProps {
	onClick: () => void;
	extraPadding?: boolean; // When used in chat - sidebar mode, there is extra padding on the banner
	appearance?: InsightsBannerAppearance;
	/** UTC ISO-8601 timestamp when the current insights were generated */
	generatedAt?: string;
}

export const InsightsBanner = ({
	onClick,
	extraPadding = false,
	appearance = 'yourWork',
	generatedAt,
}: InsightsBannerProps): React.JSX.Element => {
	const intl = useIntl();
	const { fireAnalyticsEvent } = useFireAnalyticsEvent();
	const fireAnalyticsEventRef = useRef(fireAnalyticsEvent);
	fireAnalyticsEventRef.current = fireAnalyticsEvent;
	const formattedGeneratedAt = formatGeneratedDate(
		generatedAt,
		intl.formatMessage,
		commonMessages.generatedAt,
		intl.locale,
	);

	useEffect(() => {
		fireAnalyticsEventRef.current({
			eventType: 'ui',
			actionSubject: 'insightsBanner',
			action: 'shown',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleClick = useCallback(() => {
		fireAnalyticsEvent({
			eventType: 'ui',
			actionSubject: 'insightsBanner',
			action: 'clicked',
		});
		onClick();
	}, [fireAnalyticsEvent, onClick]);

	return (
		<Pressable
			xcss={styles.cardWrapper}
			onClick={handleClick}
			aria-label={intl.formatMessage(messages.insightsBannerAriaLabel)}
			testId="rovo-insights-banner"
		>
			<Flex
				xcss={extraPadding ? styles.pressableWithExtraPadding : styles.pressable}
				alignItems="center"
				gap="space.150"
			>
				<Flex xcss={styles.iconTileSurface} alignItems="center" justifyContent="center">
					<PulseTile
						size="medium"
						appearance={appearance === 'quickActions' ? 'monochrome' : 'colorful'}
						backgroundColor="transparent"
						hasBorder
					/>
				</Flex>
				<Box xcss={styles.textContainer}>
					<Stack space="space.0">
						{formattedGeneratedAt ? (
							<Text
								size="small"
								color="color.text.subtlest"
								testId="rovo-insights-banner-generated-date"
							>
								{formattedGeneratedAt}
							</Text>
						) : null}
						<Text size="medium" weight="medium">
							{intl.formatMessage(messages.insightsBannerTitle)}
						</Text>
					</Stack>
				</Box>
				<Flex xcss={styles.chevron} alignItems="center">
					<ChevronRightIcon label="" color={token('color.icon.subtle')} />
				</Flex>
			</Flex>
		</Pressable>
	);
};
